/**
 * The canonical spelling of a punch row's `action` field, and the one place
 * that maps any spelling onto it.
 *
 * Two spellings used to reach the punch table. Ordinary punches were written
 * UPPERCASE by api-time create-punch, whose request contract is uppercase;
 * punches created from an approved correction were written lowercase, because
 * review-correction stored the schema-validated request value verbatim and
 * `punchActionSchema` in @lalohr/schemas is lowercase. Nothing normalized on
 * read, so the readers disagreed: api-time's timesheet view lowercased and
 * counted both, while api-payroll, the punch history screen and the timesheet
 * calendar matched uppercase exactly and scored correction punches as zero
 * hours. api-payroll is the paid-hours path, so an approved correction could
 * under-report an employee's pay.
 *
 * UPPERCASE is canonical in storage, because that is what the punch table
 * already held (61 of 62 prod rows), what the create-punch API accepts, what
 * review-correction's own `findPrecedingPunch` DynamoDB filter matches, and
 * what the sibling `status` / GSI values on the same rows use.
 *
 * Lowercase remains the *wire* spelling for a correction request — that is the
 * contract `punchActionSchema` defines and the web client already sends.
 * `toStoredPunchAction` converts at the write boundary, so the request keeps
 * its schema spelling and the row that results is canonical.
 *
 * Readers go through `toPunchKind`, which accepts the canonical uppercase and
 * still tolerates the legacy lowercase, so no reader can silently drop a punch
 * that another reader counts. Tolerance here is a backstop, not the fix: the
 * fix is that the write side normalizes and the one pre-existing lowercase row
 * was backfilled.
 */
const STORED = {
    CLOCK_IN: 'CLOCK_IN',
    CLOCK_OUT: 'CLOCK_OUT',
    BREAK_START: 'BREAK_START',
    BREAK_END: 'BREAK_END',
};
const KIND = {
    CLOCK_IN: 'clock_in',
    CLOCK_OUT: 'clock_out',
    BREAK_START: 'break_start',
    BREAK_END: 'break_end',
};
/**
 * Normalize any accepted spelling onto the canonical stored spelling.
 * Returns null for anything that is not a punch action.
 *
 * Call this at every write boundary, so no new row can carry a non-canonical
 * action.
 */
export function toStoredPunchAction(action) {
    if (typeof action !== 'string')
        return null;
    return STORED[action.trim().toUpperCase()] ?? null;
}
/**
 * Map a stored action onto the lowercase `PunchKind` the pairing helper uses.
 * Returns null for anything that is not a punch action.
 */
export function toPunchKind(action) {
    const stored = toStoredPunchAction(action);
    return stored ? KIND[stored] : null;
}
